import {
  LicensesSchema,
  activationSchema,
} from 'renderer/features/licenses-page/schemas/licenses.schemas';
import { NotificationsSchema } from 'renderer/features/notifications/schemas/notifications.schemas';
import { TransactionsSchema } from 'renderer/features/payments-page/schemas/transaction.schemas';
import { PaymentMethodsSchema } from 'renderer/features/settings/schemas/paymentMethods.schemas';
import { z } from 'zod';
import { UserDetailsSchema } from './userDetails.schema';

// List Users
export const ListUsersResponseSchema = z.object({
  totalDocs: z.number().nonnegative(),
  docs: UserDetailsSchema.array(),
});

export type ListUsersResponse = z.infer<typeof ListUsersResponseSchema>;

// Request type can be determined at compile time, no schema needed
export interface ListUsersRequest {
  page: number;
  limit: number;
  search?: string;
  sortBy?: string;
  sortOrder: string; // Add sort order
  filter?: any; // Make this any or a specific type that matches your filter criteria
}

export const SubscriptionSchema = z.object({
  _id: z.string(),
  name: z.string().optional(),
  isOwner: z.boolean(),
  isUsable: z.boolean(),
  group: z
    .object({
      _id: z.string().nullable(),
      firstName: z.string().optional().nullable(),
      lastName: z.string().optional().nullable(),
      organization: z.string().optional().nullable(),
      email: z.string(),
      phone: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      status: z.string().optional().nullable(),
    })
    .array()
    .optional(),
  status: z.string().optional(),
  hostApp: z.string().optional(),
  serial: z.string(),
  activations: activationSchema.array().optional(),
  count: z.number().optional(),
  scheduledChange: z.string().optional(),
  billingPeriodEndDate: z.string(),
  planName: z.string(),
  isWithinFiveDays: z.boolean(),
  upgradeId: z.string().optional(),
  downgradeId: z.string().optional(),
  stripeId: z.string().optional(),
  paypalId: z.string().optional(),
  paypalUpgradeId: z.string().optional(),
  paypalDowngradeId: z.string().optional(),
});

export type Subscription = z.infer<typeof SubscriptionSchema>;

export const SubscriptionsSchema = SubscriptionSchema.array();

export type Subscriptions = z.infer<typeof SubscriptionsSchema>;

export const UpgradeSchema = z.object({
  name: z.string(),
});
export const UpgradesSchema = UpgradeSchema.array();

// Get User Details
export const GetUserDetailsResponseSchema = z.object({
  licenses: LicensesSchema,
  user: UserDetailsSchema,
  transactions: TransactionsSchema,
  subscriptions: SubscriptionsSchema,
  notifications: NotificationsSchema,
  upgrades: UpgradesSchema,
  paymentMethods: PaymentMethodsSchema,
});

export type GetUserDetailsResponse = z.infer<
  typeof GetUserDetailsResponseSchema
>;

export type GetUserDetailsRequest = string;

// Update user profile
export const UpdateSettingsResponseSchema = z.object({
  user: UserDetailsSchema,
  message: z.string(),
});

export type UpdateSettingsResponse = z.infer<
  typeof UpdateSettingsResponseSchema
>;

export interface EditUserProfileForm {
  firstName: string;
  lastName: string;
  organization: string;
  email: string;
}

export interface UpdateSettingsRequest extends EditUserProfileForm {
  userId: string;
}

// Update user security
export interface UpdateUserSecurityRequest {
  userId: string;
  currentPassword: string;
  newPassword: string;
}

export const NewPasswordSchema = z
  .object({
    showPassword: z.boolean(),
    newPassword: z
      .string()
      .min(8, 'Password must contain at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine(
    ({ newPassword, confirmPassword }) => newPassword === confirmPassword,
    {
      message: 'Password confirmation must match',
      path: ['confirmPassword'],
    },
  );

export interface AdminUpdatePasswordRequest {
  userId: string;
  newPassword: string;
}

export interface AdminVerifyAccountRequest {
  userId: string;
}

export interface AdminDeactivateAccountRequest {
  userId: string;
}

export const UpdateUserSecurityResponseSchema = z.object({
  message: z.string(),
});

export const AdminUpdatePasswordResponseSchema =
  UpdateUserSecurityResponseSchema;

export const AdminVerifyAccountResponseSchema =
  UpdateUserSecurityResponseSchema;

export const AdminDeactivateAccountResponseSchema =
  UpdateUserSecurityResponseSchema;

export type UpdateUserSecurityResponse = z.infer<
  typeof UpdateUserSecurityResponseSchema
>;

export type AdminUpdatePasswordResponse = z.infer<
  typeof AdminUpdatePasswordResponseSchema
>;

export type AdminVerifyAccountResponse = z.infer<
  typeof AdminVerifyAccountResponseSchema
>;

export type AdminDeactivateAccountResponse = z.infer<
  typeof AdminDeactivateAccountResponseSchema
>;

export const AddCustomerFormSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address'),
    organization: z.string().optional(),
    password: z.string().min(8, 'Password must be at least 8 characters long'),
    confirmPassword: z.string().min(1, 'Please confirm password'),
  })
  .refine(({ password, confirmPassword }) => password === confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

export const AddCustomerResponseSchema = z.object({
  message: z.string(),
  user: z
    .object({
      _id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .optional(),
});

export type AddCustomerRequest = z.infer<typeof AddCustomerFormSchema>;
export type AddCustomerResponse = z.infer<typeof AddCustomerResponseSchema>;
